'use strict';

var middlewareHostHeader = require('@aws-sdk/middleware-host-header');
var middlewareLogger = require('@aws-sdk/middleware-logger');
var middlewareRecursionDetection = require('@aws-sdk/middleware-recursion-detection');
var middlewareUserAgent = require('@aws-sdk/middleware-user-agent');
var configResolver = require('@smithy/config-resolver');
var core = require('@smithy/core');
var schema = require('@smithy/core/schema');
var middlewareContentLength = require('@smithy/middleware-content-length');
var middlewareEndpoint = require('@smithy/middleware-endpoint');
var middlewareRetry = require('@smithy/middleware-retry');
var smithyClient = require('@smithy/smithy-client');
var httpAuthSchemeProvider = require('./auth/httpAuthSchemeProvider');
var runtimeConfig = require('./runtimeConfig');
var regionConfigResolver = require('@aws-sdk/region-config-resolver');
var protocolHttp = require('@smithy/protocol-http');
var schemas_0 = require('./schemas/schemas_0');
var client = require('@aws-sdk/core/client');
var errors = require('./models/errors');
var SFNServiceException = require('./models/SFNServiceException');

const resolveClientEndpointParameters = (options) => {
    return Object.assign(options, {
        useDualstackEndpoint: options.useDualstackEndpoint ?? false,
        useFipsEndpoint: options.useFipsEndpoint ?? false,
        defaultSigningName: "states",
    });
};
const commonParams = {
    UseFIPS: { type: "builtInParams", name: "useFipsEndpoint" },
    Endpoint: { type: "builtInParams", name: "endpoint" },
    Region: { type: "builtInParams", name: "region" },
    UseDualStack: { type: "builtInParams", name: "useDualstackEndpoint" },
};

const getHttpAuthExtensionConfiguration = (runtimeConfig) => {
    const _httpAuthSchemes = runtimeConfig.httpAuthSchemes;
    let _httpAuthSchemeProvider = runtimeConfig.httpAuthSchemeProvider;
    let _credentials = runtimeConfig.credentials;
    return {
        setHttpAuthScheme(httpAuthScheme) {
            const index = _httpAuthSchemes.findIndex((scheme) => scheme.schemeId === httpAuthScheme.schemeId);
            if (index === -1) {
                _httpAuthSchemes.push(httpAuthScheme);
            }
            else {
                _httpAuthSchemes.splice(index, 1, httpAuthScheme);
            }
        },
        httpAuthSchemes() {
            return _httpAuthSchemes;
        },
        setHttpAuthSchemeProvider(httpAuthSchemeProvider) {
            _httpAuthSchemeProvider = httpAuthSchemeProvider;
        },
        httpAuthSchemeProvider() {
            return _httpAuthSchemeProvider;
        },
        setCredentials(credentials) {
            _credentials = credentials;
        },
        credentials() {
            return _credentials;
        },
    };
};
const resolveHttpAuthRuntimeConfig = (config) => {
    return {
        httpAuthSchemes: config.httpAuthSchemes(),
        httpAuthSchemeProvider: config.httpAuthSchemeProvider(),
        credentials: config.credentials(),
    };
};

const resolveRuntimeExtensions = (runtimeConfig, extensions) => {
    const extensionConfiguration = Object.assign(regionConfigResolver.getAwsRegionExtensionConfiguration(runtimeConfig), smithyClient.getDefaultExtensionConfiguration(runtimeConfig), protocolHttp.getHttpHandlerExtensionConfiguration(runtimeConfig), getHttpAuthExtensionConfiguration(runtimeConfig));
    extensions.forEach((extension) => extension.configure(extensionConfiguration));
    return Object.assign(runtimeConfig, regionConfigResolver.resolveAwsRegionExtensionConfiguration(extensionConfiguration), smithyClient.resolveDefaultRuntimeConfig(extensionConfiguration), protocolHttp.resolveHttpHandlerRuntimeConfig(extensionConfiguration), resolveHttpAuthRuntimeConfig(extensionConfiguration));
};

class SFNClient extends smithyClient.Client {
    config;
    constructor(...[configuration]) {
        const _config_0 = runtimeConfig.getRuntimeConfig(configuration || {});
        super(_config_0);
        this.initConfig = _config_0;
        const _config_1 = resolveClientEndpointParameters(_config_0);
        const _config_2 = middlewareUserAgent.resolveUserAgentConfig(_config_1);
        const _config_3 = middlewareRetry.resolveRetryConfig(_config_2);
        const _config_4 = configResolver.resolveRegionConfig(_config_3);
        const _config_5 = middlewareHostHeader.resolveHostHeaderConfig(_config_4);
        const _config_6 = middlewareEndpoint.resolveEndpointConfig(_config_5);
        const _config_7 = httpAuthSchemeProvider.resolveHttpAuthSchemeConfig(_config_6);
        const _config_8 = resolveRuntimeExtensions(_config_7, configuration?.extensions || []);
        this.config = _config_8;
        this.middlewareStack.use(schema.getSchemaSerdePlugin(this.config));
        this.middlewareStack.use(middlewareUserAgent.getUserAgentPlugin(this.config));
        this.middlewareStack.use(middlewareRetry.getRetryPlugin(this.config));
        this.middlewareStack.use(middlewareContentLength.getContentLengthPlugin(this.config));
        this.middlewareStack.use(middlewareHostHeader.getHostHeaderPlugin(this.config));
        this.middlewareStack.use(middlewareLogger.getLoggerPlugin(this.config));
        this.middlewareStack.use(middlewareRecursionDetection.getRecursionDetectionPlugin(this.config));
        this.middlewareStack.use(core.getHttpAuthSchemeEndpointRuleSetPlugin(this.config, {
            httpAuthSchemeParametersProvider: httpAuthSchemeProvider.defaultSFNHttpAuthSchemeParametersProvider,
            identityProviderConfigProvider: async (config) => new core.DefaultIdentityProviderConfig({
                "aws.auth#sigv4": config.credentials,
            }),
        }));
        this.middlewareStack.use(core.getHttpSigningPlugin(this.config));
    }
    destroy() {
        super.destroy();
    }
}

class CreateActivityCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "CreateActivity", {})
    .n("SFNClient", "CreateActivityCommand")
    .sc(schemas_0.CreateActivity$)
    .build() {
}

class CreateStateMachineAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "CreateStateMachineAlias", {})
    .n("SFNClient", "CreateStateMachineAliasCommand")
    .sc(schemas_0.CreateStateMachineAlias$)
    .build() {
}

class CreateStateMachineCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "CreateStateMachine", {})
    .n("SFNClient", "CreateStateMachineCommand")
    .sc(schemas_0.CreateStateMachine$)
    .build() {
}

class DeleteActivityCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DeleteActivity", {})
    .n("SFNClient", "DeleteActivityCommand")
    .sc(schemas_0.DeleteActivity$)
    .build() {
}

class DeleteStateMachineAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DeleteStateMachineAlias", {})
    .n("SFNClient", "DeleteStateMachineAliasCommand")
    .sc(schemas_0.DeleteStateMachineAlias$)
    .build() {
}

class DeleteStateMachineCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DeleteStateMachine", {})
    .n("SFNClient", "DeleteStateMachineCommand")
    .sc(schemas_0.DeleteStateMachine$)
    .build() {
}

class DeleteStateMachineVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DeleteStateMachineVersion", {})
    .n("SFNClient", "DeleteStateMachineVersionCommand")
    .sc(schemas_0.DeleteStateMachineVersion$)
    .build() {
}

class DescribeActivityCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DescribeActivity", {})
    .n("SFNClient", "DescribeActivityCommand")
    .sc(schemas_0.DescribeActivity$)
    .build() {
}

class DescribeExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DescribeExecution", {})
    .n("SFNClient", "DescribeExecutionCommand")
    .sc(schemas_0.DescribeExecution$)
    .build() {
}

class DescribeMapRunCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DescribeMapRun", {})
    .n("SFNClient", "DescribeMapRunCommand")
    .sc(schemas_0.DescribeMapRun$)
    .build() {
}

class DescribeStateMachineAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DescribeStateMachineAlias", {})
    .n("SFNClient", "DescribeStateMachineAliasCommand")
    .sc(schemas_0.DescribeStateMachineAlias$)
    .build() {
}

class DescribeStateMachineCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DescribeStateMachine", {})
    .n("SFNClient", "DescribeStateMachineCommand")
    .sc(schemas_0.DescribeStateMachine$)
    .build() {
}

class DescribeStateMachineForExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "DescribeStateMachineForExecution", {})
    .n("SFNClient", "DescribeStateMachineForExecutionCommand")
    .sc(schemas_0.DescribeStateMachineForExecution$)
    .build() {
}

class GetActivityTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [
        middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions()),
        client.getLongPollPlugin(config),
    ];
})
    .s("AWSStepFunctions", "GetActivityTask", {})
    .n("SFNClient", "GetActivityTaskCommand")
    .sc(schemas_0.GetActivityTask$)
    .build() {
}

class GetExecutionHistoryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "GetExecutionHistory", {})
    .n("SFNClient", "GetExecutionHistoryCommand")
    .sc(schemas_0.GetExecutionHistory$)
    .build() {
}

class ListActivitiesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ListActivities", {})
    .n("SFNClient", "ListActivitiesCommand")
    .sc(schemas_0.ListActivities$)
    .build() {
}

class ListExecutionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ListExecutions", {})
    .n("SFNClient", "ListExecutionsCommand")
    .sc(schemas_0.ListExecutions$)
    .build() {
}

class ListMapRunsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ListMapRuns", {})
    .n("SFNClient", "ListMapRunsCommand")
    .sc(schemas_0.ListMapRuns$)
    .build() {
}

class ListStateMachineAliasesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ListStateMachineAliases", {})
    .n("SFNClient", "ListStateMachineAliasesCommand")
    .sc(schemas_0.ListStateMachineAliases$)
    .build() {
}

class ListStateMachinesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ListStateMachines", {})
    .n("SFNClient", "ListStateMachinesCommand")
    .sc(schemas_0.ListStateMachines$)
    .build() {
}

class ListStateMachineVersionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ListStateMachineVersions", {})
    .n("SFNClient", "ListStateMachineVersionsCommand")
    .sc(schemas_0.ListStateMachineVersions$)
    .build() {
}

class ListTagsForResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ListTagsForResource", {})
    .n("SFNClient", "ListTagsForResourceCommand")
    .sc(schemas_0.ListTagsForResource$)
    .build() {
}

class PublishStateMachineVersionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "PublishStateMachineVersion", {})
    .n("SFNClient", "PublishStateMachineVersionCommand")
    .sc(schemas_0.PublishStateMachineVersion$)
    .build() {
}

class RedriveExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "RedriveExecution", {})
    .n("SFNClient", "RedriveExecutionCommand")
    .sc(schemas_0.RedriveExecution$)
    .build() {
}

class SendTaskFailureCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "SendTaskFailure", {})
    .n("SFNClient", "SendTaskFailureCommand")
    .sc(schemas_0.SendTaskFailure$)
    .build() {
}

class SendTaskHeartbeatCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "SendTaskHeartbeat", {})
    .n("SFNClient", "SendTaskHeartbeatCommand")
    .sc(schemas_0.SendTaskHeartbeat$)
    .build() {
}

class SendTaskSuccessCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "SendTaskSuccess", {})
    .n("SFNClient", "SendTaskSuccessCommand")
    .sc(schemas_0.SendTaskSuccess$)
    .build() {
}

class StartExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "StartExecution", {})
    .n("SFNClient", "StartExecutionCommand")
    .sc(schemas_0.StartExecution$)
    .build() {
}

class StartSyncExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "StartSyncExecution", {})
    .n("SFNClient", "StartSyncExecutionCommand")
    .sc(schemas_0.StartSyncExecution$)
    .build() {
}

class StopExecutionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "StopExecution", {})
    .n("SFNClient", "StopExecutionCommand")
    .sc(schemas_0.StopExecution$)
    .build() {
}

class TagResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "TagResource", {})
    .n("SFNClient", "TagResourceCommand")
    .sc(schemas_0.TagResource$)
    .build() {
}

class TestStateCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "TestState", {})
    .n("SFNClient", "TestStateCommand")
    .sc(schemas_0.TestState$)
    .build() {
}

class UntagResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "UntagResource", {})
    .n("SFNClient", "UntagResourceCommand")
    .sc(schemas_0.UntagResource$)
    .build() {
}

class UpdateMapRunCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "UpdateMapRun", {})
    .n("SFNClient", "UpdateMapRunCommand")
    .sc(schemas_0.UpdateMapRun$)
    .build() {
}

class UpdateStateMachineAliasCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "UpdateStateMachineAlias", {})
    .n("SFNClient", "UpdateStateMachineAliasCommand")
    .sc(schemas_0.UpdateStateMachineAlias$)
    .build() {
}

class UpdateStateMachineCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "UpdateStateMachine", {})
    .n("SFNClient", "UpdateStateMachineCommand")
    .sc(schemas_0.UpdateStateMachine$)
    .build() {
}

class ValidateStateMachineDefinitionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("AWSStepFunctions", "ValidateStateMachineDefinition", {})
    .n("SFNClient", "ValidateStateMachineDefinitionCommand")
    .sc(schemas_0.ValidateStateMachineDefinition$)
    .build() {
}

const paginateGetExecutionHistory = core.createPaginator(SFNClient, GetExecutionHistoryCommand, "nextToken", "nextToken", "maxResults");

const paginateListActivities = core.createPaginator(SFNClient, ListActivitiesCommand, "nextToken", "nextToken", "maxResults");

const paginateListExecutions = core.createPaginator(SFNClient, ListExecutionsCommand, "nextToken", "nextToken", "maxResults");

const paginateListMapRuns = core.createPaginator(SFNClient, ListMapRunsCommand, "nextToken", "nextToken", "maxResults");

const paginateListStateMachines = core.createPaginator(SFNClient, ListStateMachinesCommand, "nextToken", "nextToken", "maxResults");

const commands = {
    CreateActivityCommand,
    CreateStateMachineCommand,
    CreateStateMachineAliasCommand,
    DeleteActivityCommand,
    DeleteStateMachineCommand,
    DeleteStateMachineAliasCommand,
    DeleteStateMachineVersionCommand,
    DescribeActivityCommand,
    DescribeExecutionCommand,
    DescribeMapRunCommand,
    DescribeStateMachineCommand,
    DescribeStateMachineAliasCommand,
    DescribeStateMachineForExecutionCommand,
    GetActivityTaskCommand,
    GetExecutionHistoryCommand,
    ListActivitiesCommand,
    ListExecutionsCommand,
    ListMapRunsCommand,
    ListStateMachineAliasesCommand,
    ListStateMachinesCommand,
    ListStateMachineVersionsCommand,
    ListTagsForResourceCommand,
    PublishStateMachineVersionCommand,
    RedriveExecutionCommand,
    SendTaskFailureCommand,
    SendTaskHeartbeatCommand,
    SendTaskSuccessCommand,
    StartExecutionCommand,
    StartSyncExecutionCommand,
    StopExecutionCommand,
    TagResourceCommand,
    TestStateCommand,
    UntagResourceCommand,
    UpdateMapRunCommand,
    UpdateStateMachineCommand,
    UpdateStateMachineAliasCommand,
    ValidateStateMachineDefinitionCommand,
};
const paginators = {
    paginateGetExecutionHistory,
    paginateListActivities,
    paginateListExecutions,
    paginateListMapRuns,
    paginateListStateMachines,
};
class SFN extends SFNClient {
}
smithyClient.createAggregatedClient(commands, SFN, { paginators });

const EncryptionType = {
    AWS_OWNED_KEY: "AWS_OWNED_KEY",
    CUSTOMER_MANAGED_KMS_KEY: "CUSTOMER_MANAGED_KMS_KEY",
};
const LogLevel = {
    ALL: "ALL",
    ERROR: "ERROR",
    FATAL: "FATAL",
    OFF: "OFF",
};
const StateMachineType = {
    EXPRESS: "EXPRESS",
    STANDARD: "STANDARD",
};
const ValidationExceptionReason = {
    API_DOES_NOT_SUPPORT_LABELED_ARNS: "API_DOES_NOT_SUPPORT_LABELED_ARNS",
    CANNOT_UPDATE_COMPLETED_MAP_RUN: "CANNOT_UPDATE_COMPLETED_MAP_RUN",
    INVALID_ROUTING_CONFIGURATION: "INVALID_ROUTING_CONFIGURATION",
    MISSING_REQUIRED_PARAMETER: "MISSING_REQUIRED_PARAMETER",
};
const IncludedData = {
    ALL_DATA: "ALL_DATA",
    METADATA_ONLY: "METADATA_ONLY",
};
const ExecutionRedriveStatus = {
    NOT_REDRIVABLE: "NOT_REDRIVABLE",
    REDRIVABLE: "REDRIVABLE",
    REDRIVABLE_BY_MAP_RUN: "REDRIVABLE_BY_MAP_RUN",
};
const ExecutionStatus = {
    ABORTED: "ABORTED",
    FAILED: "FAILED",
    PENDING_REDRIVE: "PENDING_REDRIVE",
    RUNNING: "RUNNING",
    SUCCEEDED: "SUCCEEDED",
    TIMED_OUT: "TIMED_OUT",
};
const KmsKeyState = {
    CREATING: "CREATING",
    DISABLED: "DISABLED",
    PENDING_DELETION: "PENDING_DELETION",
    PENDING_IMPORT: "PENDING_IMPORT",
    UNAVAILABLE: "UNAVAILABLE",
};
const MapRunStatus = {
    ABORTED: "ABORTED",
    FAILED: "FAILED",
    RUNNING: "RUNNING",
    SUCCEEDED: "SUCCEEDED",
};
const StateMachineStatus = {
    ACTIVE: "ACTIVE",
    DELETING: "DELETING",
};
const HistoryEventType = {
    ActivityFailed: "ActivityFailed",
    ActivityScheduleFailed: "ActivityScheduleFailed",
    ActivityScheduled: "ActivityScheduled",
    ActivityStarted: "ActivityStarted",
    ActivitySucceeded: "ActivitySucceeded",
    ActivityTimedOut: "ActivityTimedOut",
    ChoiceStateEntered: "ChoiceStateEntered",
    ChoiceStateExited: "ChoiceStateExited",
    EvaluationFailed: "EvaluationFailed",
    ExecutionAborted: "ExecutionAborted",
    ExecutionFailed: "ExecutionFailed",
    ExecutionRedriven: "ExecutionRedriven",
    ExecutionStarted: "ExecutionStarted",
    ExecutionSucceeded: "ExecutionSucceeded",
    ExecutionTimedOut: "ExecutionTimedOut",
    FailStateEntered: "FailStateEntered",
    LambdaFunctionFailed: "LambdaFunctionFailed",
    LambdaFunctionScheduleFailed: "LambdaFunctionScheduleFailed",
    LambdaFunctionScheduled: "LambdaFunctionScheduled",
    LambdaFunctionStartFailed: "LambdaFunctionStartFailed",
    LambdaFunctionStarted: "LambdaFunctionStarted",
    LambdaFunctionSucceeded: "LambdaFunctionSucceeded",
    LambdaFunctionTimedOut: "LambdaFunctionTimedOut",
    MapIterationAborted: "MapIterationAborted",
    MapIterationFailed: "MapIterationFailed",
    MapIterationStarted: "MapIterationStarted",
    MapIterationSucceeded: "MapIterationSucceeded",
    MapRunAborted: "MapRunAborted",
    MapRunFailed: "MapRunFailed",
    MapRunRedriven: "MapRunRedriven",
    MapRunStarted: "MapRunStarted",
    MapRunSucceeded: "MapRunSucceeded",
    MapStateAborted: "MapStateAborted",
    MapStateEntered: "MapStateEntered",
    MapStateExited: "MapStateExited",
    MapStateFailed: "MapStateFailed",
    MapStateStarted: "MapStateStarted",
    MapStateSucceeded: "MapStateSucceeded",
    ParallelStateAborted: "ParallelStateAborted",
    ParallelStateEntered: "ParallelStateEntered",
    ParallelStateExited: "ParallelStateExited",
    ParallelStateFailed: "ParallelStateFailed",
    ParallelStateStarted: "ParallelStateStarted",
    ParallelStateSucceeded: "ParallelStateSucceeded",
    PassStateEntered: "PassStateEntered",
    PassStateExited: "PassStateExited",
    SucceedStateEntered: "SucceedStateEntered",
    SucceedStateExited: "SucceedStateExited",
    TaskFailed: "TaskFailed",
    TaskScheduled: "TaskScheduled",
    TaskStartFailed: "TaskStartFailed",
    TaskStarted: "TaskStarted",
    TaskStateAborted: "TaskStateAborted",
    TaskStateEntered: "TaskStateEntered",
    TaskStateExited: "TaskStateExited",
    TaskSubmitFailed: "TaskSubmitFailed",
    TaskSubmitted: "TaskSubmitted",
    TaskSucceeded: "TaskSucceeded",
    TaskTimedOut: "TaskTimedOut",
    WaitStateAborted: "WaitStateAborted",
    WaitStateEntered: "WaitStateEntered",
    WaitStateExited: "WaitStateExited",
};
const ExecutionRedriveFilter = {
    NOT_REDRIVEN: "NOT_REDRIVEN",
    REDRIVEN: "REDRIVEN",
};
const SyncExecutionStatus = {
    FAILED: "FAILED",
    SUCCEEDED: "SUCCEEDED",
    TIMED_OUT: "TIMED_OUT",
};
const InspectionLevel = {
    DEBUG: "DEBUG",
    INFO: "INFO",
    TRACE: "TRACE",
};
const MockResponseValidationMode = {
    NONE: "NONE",
    PRESENT: "PRESENT",
    STRICT: "STRICT",
};
const TestExecutionStatus = {
    CAUGHT_ERROR: "CAUGHT_ERROR",
    FAILED: "FAILED",
    RETRIABLE: "RETRIABLE",
    SUCCEEDED: "SUCCEEDED",
};
const ValidateStateMachineDefinitionSeverity = {
    ERROR: "ERROR",
    WARNING: "WARNING",
};
const ValidateStateMachineDefinitionResultCode = {
    FAIL: "FAIL",
    OK: "OK",
};

exports.$Command = smithyClient.Command;
exports.__Client = smithyClient.Client;
exports.SFNServiceException = SFNServiceException.SFNServiceException;
exports.CreateActivityCommand = CreateActivityCommand;
exports.CreateStateMachineAliasCommand = CreateStateMachineAliasCommand;
exports.CreateStateMachineCommand = CreateStateMachineCommand;
exports.DeleteActivityCommand = DeleteActivityCommand;
exports.DeleteStateMachineAliasCommand = DeleteStateMachineAliasCommand;
exports.DeleteStateMachineCommand = DeleteStateMachineCommand;
exports.DeleteStateMachineVersionCommand = DeleteStateMachineVersionCommand;
exports.DescribeActivityCommand = DescribeActivityCommand;
exports.DescribeExecutionCommand = DescribeExecutionCommand;
exports.DescribeMapRunCommand = DescribeMapRunCommand;
exports.DescribeStateMachineAliasCommand = DescribeStateMachineAliasCommand;
exports.DescribeStateMachineCommand = DescribeStateMachineCommand;
exports.DescribeStateMachineForExecutionCommand = DescribeStateMachineForExecutionCommand;
exports.EncryptionType = EncryptionType;
exports.ExecutionRedriveFilter = ExecutionRedriveFilter;
exports.ExecutionRedriveStatus = ExecutionRedriveStatus;
exports.ExecutionStatus = ExecutionStatus;
exports.GetActivityTaskCommand = GetActivityTaskCommand;
exports.GetExecutionHistoryCommand = GetExecutionHistoryCommand;
exports.HistoryEventType = HistoryEventType;
exports.IncludedData = IncludedData;
exports.InspectionLevel = InspectionLevel;
exports.KmsKeyState = KmsKeyState;
exports.ListActivitiesCommand = ListActivitiesCommand;
exports.ListExecutionsCommand = ListExecutionsCommand;
exports.ListMapRunsCommand = ListMapRunsCommand;
exports.ListStateMachineAliasesCommand = ListStateMachineAliasesCommand;
exports.ListStateMachineVersionsCommand = ListStateMachineVersionsCommand;
exports.ListStateMachinesCommand = ListStateMachinesCommand;
exports.ListTagsForResourceCommand = ListTagsForResourceCommand;
exports.LogLevel = LogLevel;
exports.MapRunStatus = MapRunStatus;
exports.MockResponseValidationMode = MockResponseValidationMode;
exports.PublishStateMachineVersionCommand = PublishStateMachineVersionCommand;
exports.RedriveExecutionCommand = RedriveExecutionCommand;
exports.SFN = SFN;
exports.SFNClient = SFNClient;
exports.SendTaskFailureCommand = SendTaskFailureCommand;
exports.SendTaskHeartbeatCommand = SendTaskHeartbeatCommand;
exports.SendTaskSuccessCommand = SendTaskSuccessCommand;
exports.StartExecutionCommand = StartExecutionCommand;
exports.StartSyncExecutionCommand = StartSyncExecutionCommand;
exports.StateMachineStatus = StateMachineStatus;
exports.StateMachineType = StateMachineType;
exports.StopExecutionCommand = StopExecutionCommand;
exports.SyncExecutionStatus = SyncExecutionStatus;
exports.TagResourceCommand = TagResourceCommand;
exports.TestExecutionStatus = TestExecutionStatus;
exports.TestStateCommand = TestStateCommand;
exports.UntagResourceCommand = UntagResourceCommand;
exports.UpdateMapRunCommand = UpdateMapRunCommand;
exports.UpdateStateMachineAliasCommand = UpdateStateMachineAliasCommand;
exports.UpdateStateMachineCommand = UpdateStateMachineCommand;
exports.ValidateStateMachineDefinitionCommand = ValidateStateMachineDefinitionCommand;
exports.ValidateStateMachineDefinitionResultCode = ValidateStateMachineDefinitionResultCode;
exports.ValidateStateMachineDefinitionSeverity = ValidateStateMachineDefinitionSeverity;
exports.ValidationExceptionReason = ValidationExceptionReason;
exports.paginateGetExecutionHistory = paginateGetExecutionHistory;
exports.paginateListActivities = paginateListActivities;
exports.paginateListExecutions = paginateListExecutions;
exports.paginateListMapRuns = paginateListMapRuns;
exports.paginateListStateMachines = paginateListStateMachines;
Object.prototype.hasOwnProperty.call(schemas_0, '__proto__') &&
    !Object.prototype.hasOwnProperty.call(exports, '__proto__') &&
    Object.defineProperty(exports, '__proto__', {
        enumerable: true,
        value: schemas_0['__proto__']
    });

Object.keys(schemas_0).forEach(function (k) {
    if (k !== 'default' && !Object.prototype.hasOwnProperty.call(exports, k)) exports[k] = schemas_0[k];
});
Object.prototype.hasOwnProperty.call(errors, '__proto__') &&
    !Object.prototype.hasOwnProperty.call(exports, '__proto__') &&
    Object.defineProperty(exports, '__proto__', {
        enumerable: true,
        value: errors['__proto__']
    });

Object.keys(errors).forEach(function (k) {
    if (k !== 'default' && !Object.prototype.hasOwnProperty.call(exports, k)) exports[k] = errors[k];
});
